import { supabase } from '@/lib/supabase'
import type {
  Election,
  ElectionStatus,
  ElectionType,
  ElectionResults,
  ElectionWithNation,
  CreateElectionInput,
} from '@/types'

// Query key factory for consistent cache management
export const electionKeys = {
  all: ['elections'] as const,
  byNation: (nationId: string) => ['elections', 'nation', nationId] as const,
  byWorld: (worldId: string) => ['elections', 'world', worldId] as const,
  byStatus: (status: ElectionStatus) => ['elections', 'status', status] as const,
  detail: (id: string) => ['elections', 'id', id] as const,
  current: (nationId: string) => ['elections', 'current', nationId] as const,
  upcoming: (worldId: string) => ['elections', 'upcoming', worldId] as const,
}

// Default campaign length in ticks (4 ticks = 1 game year)
export const DEFAULT_CAMPAIGN_LENGTH = 4

// ============================================================================
// Core Fetch Functions
// ============================================================================

/**
 * Fetches all elections for a nation
 * Orders by scheduled_tick descending (most recent first)
 */
export async function fetchElectionsByNation(nationId: string): Promise<Election[]> {
  const { data, error } = await supabase
    .from('elections')
    .select('*')
    .eq('nation_id', nationId)
    .order('scheduled_tick', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch elections for nation: ${error.message}`)
  }

  return data as Election[]
}

/**
 * Fetches a single election by ID
 * Returns null if not found
 */
export async function fetchElectionById(electionId: string): Promise<Election | null> {
  const { data, error } = await supabase
    .from('elections')
    .select('*')
    .eq('id', electionId)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to fetch election: ${error.message}`)
  }

  return data as Election | null
}

/**
 * Fetches the current active election for a nation
 * Returns the election that is in scheduled, campaigning, or voting status
 * If multiple exist (shouldn't normally happen), returns the one with earliest scheduled_tick
 */
export async function fetchCurrentElection(nationId: string): Promise<Election | null> {
  const { data, error } = await supabase
    .from('elections')
    .select('*')
    .eq('nation_id', nationId)
    .in('status', ['scheduled', 'campaigning', 'voting'])
    .order('scheduled_tick', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to fetch current election: ${error.message}`)
  }

  return data as Election | null
}

/**
 * Fetches all upcoming elections across a world (for admin dashboard)
 * Includes elections that are scheduled or campaigning
 * Joins with nation data for display
 */
export async function fetchUpcomingElections(worldId: string): Promise<ElectionWithNation[]> {
  const { data, error } = await supabase
    .from('elections')
    .select(
      `
      *,
      nation:nations!inner (
        id,
        name,
        world_id,
        electoral_system,
        threshold_percent
      )
    `
    )
    .eq('nation.world_id', worldId)
    .in('status', ['scheduled', 'campaigning'])
    .order('scheduled_tick', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch upcoming elections: ${error.message}`)
  }

  return data as ElectionWithNation[]
}

/**
 * Fetches elections by status for a world (for tick processing)
 */
export async function fetchElectionsByStatus(
  worldId: string,
  status: ElectionStatus
): Promise<ElectionWithNation[]> {
  const { data, error } = await supabase
    .from('elections')
    .select(
      `
      *,
      nation:nations!inner (
        id,
        name,
        world_id,
        electoral_system,
        threshold_percent
      )
    `
    )
    .eq('nation.world_id', worldId)
    .eq('status', status)
    .order('scheduled_tick', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch elections by status: ${error.message}`)
  }

  return data as ElectionWithNation[]
}

/**
 * Fetches completed elections for a nation (election history)
 * Orders by scheduled_tick descending (most recent first)
 * Optional limit for pagination
 */
export async function fetchElectionHistory(
  nationId: string,
  limit: number = 10
): Promise<Election[]> {
  const { data, error } = await supabase
    .from('elections')
    .select('*')
    .eq('nation_id', nationId)
    .eq('status', 'completed')
    .order('scheduled_tick', { ascending: false })
    .limit(limit)

  if (error) {
    throw new Error(`Failed to fetch election history: ${error.message}`)
  }

  return data as Election[]
}

/**
 * Fetches the most recent completed election for a nation
 */
export async function fetchLastCompletedElection(nationId: string): Promise<Election | null> {
  const { data, error } = await supabase
    .from('elections')
    .select('*')
    .eq('nation_id', nationId)
    .eq('status', 'completed')
    .order('scheduled_tick', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to fetch last completed election: ${error.message}`)
  }

  return data as Election | null
}

// ============================================================================
// Create Functions
// ============================================================================

/**
 * Creates a new election for a nation
 * Calculates campaign_start_tick automatically based on campaign_length
 */
export async function createElection(input: CreateElectionInput): Promise<Election> {
  const {
    nation_id,
    election_type = 'legislative',
    scheduled_tick,
    campaign_length = DEFAULT_CAMPAIGN_LENGTH,
    is_early = false,
  } = input

  // Calculate campaign start tick (ensure it's not negative)
  const campaign_start_tick = Math.max(0, scheduled_tick - campaign_length)

  const { data, error } = await supabase
    .from('elections')
    .insert({
      nation_id,
      election_type,
      scheduled_tick,
      campaign_start_tick,
      status: 'scheduled',
      is_early,
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create election: ${error.message}`)
  }

  return data as Election
}

/**
 * Creates an early/snap election for a nation
 * Shorthand for createElection with is_early: true
 */
export async function createEarlyElection(
  nationId: string,
  scheduledTick: number,
  electionType: ElectionType = 'legislative',
  campaignLength: number = DEFAULT_CAMPAIGN_LENGTH
): Promise<Election> {
  return createElection({
    nation_id: nationId,
    election_type: electionType,
    scheduled_tick: scheduledTick,
    campaign_length: campaignLength,
    is_early: true,
  })
}

// ============================================================================
// Update Functions
// ============================================================================

/**
 * Updates the election status
 * Optionally includes results when transitioning to 'completed'
 */
export async function updateElectionStatus(
  electionId: string,
  status: ElectionStatus,
  results?: ElectionResults
): Promise<Election> {
  const updateData: Partial<Election> & { status: ElectionStatus } = { status }
  if (results !== undefined) {
    updateData.results = results
  }

  const { data, error } = await supabase
    .from('elections')
    .update(updateData)
    .eq('id', electionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update election status: ${error.message}`)
  }

  return data as Election
}

/**
 * Transitions election from scheduled to campaigning
 * Use when current_tick >= campaign_start_tick
 */
export async function transitionToCampaigning(electionId: string): Promise<Election> {
  return updateElectionStatus(electionId, 'campaigning')
}

/**
 * Transitions election from campaigning to voting
 * Use when current_tick >= scheduled_tick
 */
export async function transitionToVoting(electionId: string): Promise<Election> {
  return updateElectionStatus(electionId, 'voting')
}

/**
 * Completes an election with results
 * Transitions to 'completed' status and stores results
 */
export async function completeElection(
  electionId: string,
  results: ElectionResults
): Promise<Election> {
  return updateElectionStatus(electionId, 'completed', results)
}

// ============================================================================
// Bulk Operations (for tick processing)
// ============================================================================

/**
 * Fetches all elections that should transition to campaigning
 * Returns elections where status='scheduled' and current_tick >= campaign_start_tick
 */
export async function getElectionsReadyForCampaigning(
  worldId: string,
  currentTick: number
): Promise<ElectionWithNation[]> {
  const { data, error } = await supabase
    .from('elections')
    .select(
      `
      *,
      nation:nations!inner (
        id,
        name,
        world_id,
        electoral_system,
        threshold_percent
      )
    `
    )
    .eq('nation.world_id', worldId)
    .eq('status', 'scheduled')
    .lte('campaign_start_tick', currentTick)

  if (error) {
    throw new Error(`Failed to fetch elections ready for campaigning: ${error.message}`)
  }

  return data as ElectionWithNation[]
}

/**
 * Fetches all elections that should transition to voting
 * Returns elections where status='campaigning' and current_tick >= scheduled_tick
 */
export async function getElectionsReadyForVoting(
  worldId: string,
  currentTick: number
): Promise<ElectionWithNation[]> {
  const { data, error } = await supabase
    .from('elections')
    .select(
      `
      *,
      nation:nations!inner (
        id,
        name,
        world_id,
        electoral_system,
        threshold_percent
      )
    `
    )
    .eq('nation.world_id', worldId)
    .eq('status', 'campaigning')
    .lte('scheduled_tick', currentTick)

  if (error) {
    throw new Error(`Failed to fetch elections ready for voting: ${error.message}`)
  }

  return data as ElectionWithNation[]
}

/**
 * Batch transition elections to campaigning
 * Used by tick processor
 */
export async function batchTransitionToCampaigning(electionIds: string[]): Promise<void> {
  if (electionIds.length === 0) return

  const { error } = await supabase
    .from('elections')
    .update({ status: 'campaigning' as ElectionStatus })
    .in('id', electionIds)

  if (error) {
    throw new Error(`Failed to batch transition to campaigning: ${error.message}`)
  }
}

/**
 * Batch transition elections to voting
 * Used by tick processor
 */
export async function batchTransitionToVoting(electionIds: string[]): Promise<void> {
  if (electionIds.length === 0) return

  const { error } = await supabase
    .from('elections')
    .update({ status: 'voting' as ElectionStatus })
    .in('id', electionIds)

  if (error) {
    throw new Error(`Failed to batch transition to voting: ${error.message}`)
  }
}

// ============================================================================
// Query Helpers
// ============================================================================

/**
 * Checks if a nation has an upcoming or active election
 */
export async function hasActiveElection(nationId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('elections')
    .select('*', { count: 'exact', head: true })
    .eq('nation_id', nationId)
    .in('status', ['scheduled', 'campaigning', 'voting'])

  if (error) {
    throw new Error(`Failed to check active election: ${error.message}`)
  }

  return (count ?? 0) > 0
}

/**
 * Gets the count of elections by status for a nation
 */
export async function getElectionCountByStatus(
  nationId: string
): Promise<Record<ElectionStatus, number>> {
  const { data, error } = await supabase
    .from('elections')
    .select('status')
    .eq('nation_id', nationId)

  if (error) {
    throw new Error(`Failed to get election counts: ${error.message}`)
  }

  const counts: Record<ElectionStatus, number> = {
    scheduled: 0,
    campaigning: 0,
    voting: 0,
    completed: 0,
  }

  for (const row of data || []) {
    if (row.status in counts) {
      counts[row.status as ElectionStatus]++
    }
  }

  return counts
}
